/**
 * Glyph — browser bootstrap (effects + visuals + palettes + tempo + auto-pilot).
 *
 * Wiring, one direction only:
 *
 *     DOM key events ─▶ KeyState ─▶ InputController ─▶ paramBus.set()
 *                         │  (Shift latches, ~ reroll, Tab randomize, +/- modify)  │
 *                         ├─ Space ─▶ TapTempo ──────────────────▶│
 *                         ├─ \\ ─▶ AutoPilot (rolls on the beat) ──▶│
 *                         └─ Backspace ─▶ InputController.clearAll()
 *                                          paramBus.subscribe()▼
 *                     console log · legend · palette · tempo indicator · Three.js uniforms
 *
 * Every observer downstream of the bus (scene, legend, palette, tempo) only reads
 * it; none see a KeyboardEvent.
 */

import { ParamBus } from './state/paramBus';
import { KeyState } from './input/keyState';
import { InputController, RANDOMIZE_KEY } from './input/inputController';
import { AutoPilot } from './input/autoPilot';
import { ModifierController, modParam, MODIFIER_DEFAULT } from './input/modifierController';
import {
  GlobalController,
  GLOBAL_ZOOM,
  GLOBAL_SPEED,
  GLOBAL_DEFAULT,
  GLOBAL_SPEED_DEFAULT,
} from './input/globalController';
import { PaletteController } from './input/paletteController';
import { ComboDetector } from './input/comboDetector';
import { TapTempo } from './tempo/tapTempo';
import { AudioReactor } from './tempo/audioReactor';
import { attachStageGestures } from './input/stageGestures';
import { EffectDef, effectConfig, effectByKey, effectParam, latchParam } from './effects/effectConfig';
import { paletteConfig, PALETTE_TYPE_PARAM, PALETTE_HUE_PARAM } from './effects/presetConfig';
import { visualConfig, visualByKey, visualModParam, VISUAL_PARAM } from './effects/visualConfig';
import { comboConfig } from './effects/comboConfig';
import { attachDockPointer } from './input/dockPointer';
import { Visualizer, mapSpeed } from './scene/visualizer';
import { attachMirrorSender } from './mirror/mirrorLink';

/**
 * PanelController — the on-screen control panel's visibility.
 *
 * Three lock states, cycled by Enter (or clicking the Enter cell / reveal
 * handle): `auto` → `open` → `hidden` → `auto`.
 *   auto   — visible, but fades out after IDLE_MS of no key/pointer activity;
 *            any activity (or hovering the dock) brings it back.
 *   open   — pinned visible, idle timer disabled.
 *   hidden — tucked away; a small reveal handle stays on-screen to bring it back.
 * Input still flows regardless of visibility — this only touches the dock's DOM.
 * The Enter cell's caption reflects the state (Panel / Locked / Hidden).
 */
type LockState = 'auto' | 'open' | 'hidden';

class PanelController {
  private state: LockState = 'auto';
  private idleTimer: ReturnType<typeof setTimeout> | undefined;
  private pointerInside = false;
  private static readonly IDLE_MS = 4000;

  constructor(
    private readonly dock: HTMLElement,
    private readonly lockCell?: HTMLElement,
    private readonly reveal?: HTMLElement,
    private readonly lockBtn?: HTMLElement,
    private readonly collapseBtn?: HTMLElement,
  ) {
    const activity = (): void => this.onActivity();
    window.addEventListener('pointermove', activity, { passive: true });
    window.addEventListener('pointerdown', activity, { passive: true });
    window.addEventListener('touchstart', activity, { passive: true });

    // Hovering the dock pins it open so it can be read / clicked.
    dock.addEventListener('pointerenter', () => { this.pointerInside = true; this.clearIdle(); });
    dock.addEventListener('pointerleave', () => { this.pointerInside = false; this.armIdle(); });

    this.lockCell?.addEventListener('click', () => this.cycle());
    this.reveal?.addEventListener('click', () => this.cycle());
    // Dock top-right icons: lock pins open (auto ↔ open); collapse tucks it away.
    this.lockBtn?.addEventListener('click', () => this.togglePin());
    this.collapseBtn?.addEventListener('click', () => this.toggleCollapsed());

    this.apply();
  }

  /** Key or pointer activity — in auto mode, reveal and restart the idle timer. */
  onActivity(): void {
    if (this.state !== 'auto') return;
    this.dock.classList.remove('is-hidden');
    this.armIdle();
  }

  /** Enter / click: auto → open → hidden → auto. */
  cycle(): void {
    this.state = this.state === 'auto' ? 'open' : this.state === 'open' ? 'hidden' : 'auto';
    this.apply();
  }

  /** Long-press the canvas / collapse icon: toggle between tucked-away and open. */
  toggleCollapsed(): void {
    this.state = this.state === 'hidden' ? 'open' : 'hidden';
    this.apply();
  }

  /** Lock icon: pin the panel open, or release it back to auto-hide. */
  togglePin(): void {
    this.state = this.state === 'open' ? 'auto' : 'open';
    this.apply();
  }

  private apply(): void {
    this.clearIdle();
    document.body.classList.toggle('panel-locked-hidden', this.state === 'hidden');
    if (this.state === 'hidden') this.dock.classList.add('is-hidden');
    else this.dock.classList.remove('is-hidden');
    if (this.state === 'auto') this.armIdle();
    this.reflectLock();
  }

  private armIdle(): void {
    this.clearIdle();
    if (this.state !== 'auto' || this.pointerInside) return;
    this.idleTimer = setTimeout(() => this.dock.classList.add('is-hidden'), PanelController.IDLE_MS);
  }

  private clearIdle(): void {
    if (this.idleTimer !== undefined) { clearTimeout(this.idleTimer); this.idleTimer = undefined; }
  }

  private reflectLock(): void {
    // The top-right padlock closes (amber) whenever the panel is pinned open.
    this.lockBtn?.classList.toggle('is-locked', this.state === 'open');
    if (!this.lockCell) return;
    this.lockCell.classList.toggle('state-open', this.state === 'open');
    this.lockCell.classList.toggle('state-hidden', this.state === 'hidden');
    const caption = this.lockCell.querySelector('.name');
    if (caption) caption.textContent = this.state === 'open' ? 'Locked' : this.state === 'hidden' ? 'Hidden' : 'Panel';
    this.lockCell.title =
      this.state === 'auto'   ? 'Enter — panel auto-hides (click to lock open)'
    : this.state === 'open'   ? 'Enter — panel locked open (click to hide)'
    :                           'Enter — panel hidden (click for auto-hide)';
  }
}

/** Briefly flash a modifier cell's pressed glow (for momentary keys like Tab/⌫). */
function flashCell(cell: HTMLElement | undefined, ms = 180): void {
  if (!cell) return;
  cell.classList.add('pressed');
  setTimeout(() => cell.classList.remove('pressed'), ms);
}

const bus = new ParamBus({ log: true });

// Seed the default base visual and neutral per-effect modifiers so the scene
// starts on visual 0 (Oscilloscope) with every effect's modifier at its neutral
// midpoint. The palette is seeded by palette.reset() below (Spectrum = the
// original rainbow look).
bus.set(VISUAL_PARAM, 0);
for (const def of effectConfig) bus.set(modParam(def.effect), def.modDefault ?? MODIFIER_DEFAULT);
for (const v of visualConfig) bus.set(visualModParam(v.id), MODIFIER_DEFAULT);
// Global camera transforms ([ ] zoom, < > speed) start neutral (1x).
bus.set(GLOBAL_ZOOM, GLOBAL_DEFAULT);
bus.set(GLOBAL_SPEED, GLOBAL_SPEED_DEFAULT);

const controller = new InputController(bus);
const tapTempo = new TapTempo(bus);
// Music reactivity (the `?` key): mic → beat clock, overriding tap tempo. It writes
// only tempo.anchorMs / tempo.bpm, so every beat-locked effect reacts for free.
const audioReactor = new AudioReactor(bus);
const combos = new ComboDetector(bus);
// Targets whichever effect key is held when +/- is pressed; fed the key stream below.
const modifiers = new ModifierController(bus);
// A second knob for the base visuals: hold a number key (1–7) and +/- scrubs its
// `vmod.<id>`. Same class, bound to the visual key→id map and the vmod namespace.
const visualTargets = new Map([...visualByKey].map(([k, v]) => [k, v.id]));
const visualMod = new ModifierController(bus, visualTargets, visualModParam);
// Persistent global camera axes: [ ] dolly zoom, < > animation speed (< reverses).
const globals = new GlobalController(bus);
// The four keycaps GlobalController owns — used only to light their dock cells on hold.
const GLOBAL_KEYS = new Set(['BracketLeft', 'BracketRight', 'Comma', 'Period']);
// Owns palette-type selection (8–0) and each type's base hue (+/- while held).
const palette = new PaletteController(bus);
palette.reset(); // seed palette.type + palette.hue on the default (Spectrum)
// `\` hands-free mode: rolls a fresh random effect set on every beat.
const autoPilot = new AutoPilot(bus, {
  onBeat: () => controller.rollAuto(),
  onStop: () => controller.stopAuto(),
});

// The dock is built before KeyState so the key handlers can light its modifier
// cells and Enter can drive the panel. It reads the bus for effect highlights.
const kb = buildKeyboard();
const dockEl = document.querySelector('.dock');
const revealEl = document.getElementById('panel-reveal');
const lockBtnEl = document.getElementById('dock-lock');
const collapseBtnEl = document.getElementById('dock-collapse');
const panel =
  dockEl instanceof HTMLElement
    ? new PanelController(
        dockEl,
        kb.lockCell,
        revealEl instanceof HTMLElement ? revealEl : undefined,
        lockBtnEl instanceof HTMLElement ? lockBtnEl : undefined,
        collapseBtnEl instanceof HTMLElement ? collapseBtnEl : undefined,
      )
    : undefined;
// Reflect the `\` auto-pilot cell's on/off state after any toggle or reset.
const syncAutoPilotCell = (): void => { kb.mods.get('Backslash')?.classList.toggle('on', autoPilot.isActive); };
// Reflect the `?` music-reactivity cell's on/off state after any toggle or reset.
const syncMusicCell = (): void => { kb.mods.get('Slash')?.classList.toggle('on', audioReactor.active); };

const keyState = new KeyState({
  onDown: (key, e) => {
    // Any keypress counts as activity for the auto-hiding panel.
    panel?.onActivity();
    // Enter drives the panel lock (auto → open → hidden); not an effect key.
    if (key === 'Enter') { e.preventDefault(); panel?.cycle(); return; }
    // The combo detector and the modifier controller both observe the raw key
    // stream alongside everything else and write only their own params — neither
    // short-circuits normal input, so combo keys still fire their individual
    // effects, and the modifier just notes which effect key is being held.
    combos.keyDown(key);
    modifiers.keyDown(key);
    visualMod.keyDown(key);
    // Persistent global camera axes: [ ] zoom, < > speed (ramped in the render loop).
    globals.keyDown(key);
    // Music reactivity captures +/- to scrub detection sensitivity (only while active).
    audioReactor.keyDown(key);
    // Palette owns 8–0 selection and, while a palette key is held, the +/- base-hue shift.
    palette.keyDown(key, performance.now());
    if (key === 'Space') {
      e.preventDefault();
      kb.mods.get('Space')?.classList.add('pressed');
      tapTempo.spaceDown();
      return;
    }
    if (key === 'Backspace' || key === 'Delete') {
      // Clear every active effect back to its resting state.
      e.preventDefault();
      flashCell(kb.mods.get(key));
      controller.clearAll();
      return;
    }
    if (key === 'Escape') {
      // Panic reset: drop all effects and return to the default look.
      controller.clearAll();
      bus.set(VISUAL_PARAM, 0);
      palette.reset();
      globals.reset(); // zoom + speed back to neutral (1x)
      autoPilot.stop();
      syncAutoPilotCell();
      audioReactor.stop();
      syncMusicCell();
      return;
    }
    if (key === 'Backquote') {
      // ~ rerolls the whole look: a fresh base visual + palette.
      flashCell(kb.mods.get('Backquote'));
      controller.rerollLook(); // random base visual
      palette.randomize();     // random palette type (keeps each type's hue)
      return;
    }
    if (key === 'Backslash') {
      autoPilot.toggle();
      syncAutoPilotCell();
      return;
    }
    if (key === 'Slash') {
      // ? toggles music reactivity (mic → beat clock). Async: reflect the cell once
      // the mic actually opens (or fails), so a denied permission leaves it unlit.
      audioReactor.toggle().then(syncMusicCell);
      return;
    }
    // Momentary glow for the held modifier cells (Tab randomize, Shift latch, +/- tune).
    // Tab is the randomize key now, so it flows on to handleKeyDown below (and we
    // preventDefault so it doesn't move focus off the canvas).
    if (key === 'Tab') { e.preventDefault(); kb.mods.get('Tab')?.classList.add('pressed'); }
    else if (key === 'Minus' || key === 'Equal') kb.mods.get(key)?.classList.add('pressed');
    else if (GLOBAL_KEYS.has(key)) kb.mods.get(key)?.classList.add('pressed');
    else if (key.startsWith('Shift')) kb.mods.get(key)?.classList.add('pressed');
    controller.handleKeyDown(key, { shift: e.shiftKey });
  },
  onUp: (key, durationMs) => {
    panel?.onActivity();
    combos.keyUp(key);
    modifiers.keyUp(key);
    visualMod.keyUp(key);
    globals.keyUp(key);
    audioReactor.keyUp(key);
    palette.keyUp(key);
    if (key === 'Space') {
      kb.mods.get('Space')?.classList.remove('pressed');
      tapTempo.spaceUp(durationMs);
      return;
    }
    if (key === 'Tab') kb.mods.get('Tab')?.classList.remove('pressed');
    else if (key === 'Minus' || key === 'Equal') kb.mods.get(key)?.classList.remove('pressed');
    else if (GLOBAL_KEYS.has(key)) kb.mods.get(key)?.classList.remove('pressed');
    else if (key.startsWith('Shift')) kb.mods.get(key)?.classList.remove('pressed');
    controller.handleKeyUp(key, durationMs);
  },
});
keyState.attach(window);

// Make the dock clickable / tappable (tablet use): pointer taps on its cells are
// adapted into the same KeyState stream as physical keys — see dockPointer. Sticky
// Shift + tap-to-tune keep latching and +/- usable with a single pointer.
const keyboardEl = document.getElementById('keyboard');
if (keyboardEl) {
  const shiftCells = [kb.mods.get('ShiftLeft'), kb.mods.get('ShiftRight')].filter(
    (c): c is HTMLElement => c instanceof HTMLElement,
  );
  attachDockPointer({ root: keyboardEl, keyState, modifiers, visualMod, palette, shiftCells });
}

// Canvas gestures (iPad-first) — the empty screen behind the dock is a gesture
// surface. A plain tap still sets the tempo (tap-only, never the Space hold-for-live
// path); touch adds: long-press to toggle the dock (collapse + lock ↔ reopen),
// 1-finger swipes to step the base visual (L/R) or palette (U/D), and a 2-finger
// press to randomize while held — wired straight to keyState.press/release('Tab', …),
// the same synthetic-event seam dockPointer uses for dock taps, so it's really just
// Tab's own momentary hold (random effects on, cleared on release) triggered by a
// second finger touching down instead of a keypress.
// A mouse keeps tap-to-tempo only. See input/stageGestures. (The dock captures its
// own taps and is pointer-events:none when hidden, so gestures fall through here.)
const stageEl = document.getElementById('stage');
if (stageEl) {
  attachStageGestures({
    stage: stageEl,
    onTapTempo: () => tapTempo.tap(performance.now()),
    onLongPress: () => panel?.toggleCollapsed(),
    onStepVisual: (dir) => controller.stepVisual(dir),
    onStepPalette: (dir) => palette.step(dir),
    onRandomizeStart: () => keyState.press(RANDOMIZE_KEY, new KeyboardEvent('keydown')),
    onRandomizeEnd: () => keyState.release(RANDOMIZE_KEY, new KeyboardEvent('keyup')),
  });
}

// The label above the −/+ keys, showing what a +/- scrub would tune right now.
// Matches the +/- capture precedence: a held palette key wins ("Triadic · Base
// hue"), then a held visual key ("Oil · Swirl"), then the held effect + its
// modifier ("Kaleido · Segments").
const modifyLabelEl = document.getElementById('modify-label');
const effectByEffect = new Map(effectConfig.map((d) => [d.effect, d]));
const visualByIdLabel = new Map(visualConfig.map((v) => [v.id, v]));
const paletteLabelByIndex = new Map(paletteConfig.map((p) => [p.index, p.label]));
const paletteIdByIndex = new Map(paletteConfig.map((p) => [p.index, p.id]));
// Build the label's inner structure once: a name span, a live value readout, and
// a thin meter that fills with the current 0..1 value so the change is visible.
let modifyLblEl: HTMLElement | undefined;
let modifyValEl: HTMLElement | undefined;
let modifyFillEl: HTMLElement | undefined;
if (modifyLabelEl) {
  modifyLabelEl.innerHTML =
    '<span class="lbl"></span><span class="mod-val"></span>' +
    '<span class="mod-meter"><span class="mod-fill"></span></span>';
  modifyLblEl = modifyLabelEl.querySelector('.lbl') as HTMLElement;
  modifyValEl = modifyLabelEl.querySelector('.mod-val') as HTMLElement;
  modifyFillEl = modifyLabelEl.querySelector('.mod-fill') as HTMLElement;
}
let modifyLabelText = '';
let modifyValText = '';
let modifyFillBg = '';
let modifyLastValue = -1;
// Matches the +/- capture precedence: a held palette key wins (the actual palette
// colours at the current base hue), then a held visual key, then the held effect +
// its modifier (0..100%).
const refreshModifyLabel = (): void => {
  if (!modifyLabelEl) return;
  let text = '';
  let valText = '';
  let fill = 0;        // meter width, 0..1
  let swatchBg = '';   // when set, the meter shows this gradient instead of a level
  let changeVal = 0;   // the underlying value watched for "is changing" feedback
  const pt = palette.target;
  const vt = visualMod.target;
  const axis = globals.activeAxis();
  if (axis !== undefined) {
    // Holding [ ] < > shows the global axis in the same slot as the −/+ knob.
    const isSpeed = axis === GLOBAL_SPEED;
    const v = bus.get(axis, isSpeed ? GLOBAL_SPEED_DEFAULT : GLOBAL_DEFAULT);
    text = isSpeed ? 'FX Speed' : 'Zoom';
    // Speed is symmetric around a freeze at 50%, so a raw % is misleading — show
    // the signed time multiplier (e.g. 1.0×, 0.0×, -3.0×). Zoom keeps its %.
    valText = isSpeed ? `${mapSpeed(v).toFixed(1)}×` : `${Math.round(v * 100)}%`;
    fill = v;
    changeVal = v;
  } else if (pt !== undefined) {
    // While a palette key is held its type is selected, so the bus hue is its hue.
    const v = bus.get(PALETTE_HUE_PARAM, 0);
    text = `${paletteLabelByIndex.get(pt) ?? 'Palette'} · Base hue`;
    valText = `${Math.round(v * 360)}°`;
    // Show the real colours being selected: the palette's gradient at this hue,
    // filling the whole meter (a hue has no meaningful 0..100% level).
    swatchBg = paletteSwatch(paletteIdByIndex.get(pt) ?? 'spectrum', v);
    fill = 1;
    changeVal = v;
  } else if (vt !== undefined) {
    const vdef = visualByIdLabel.get(vt);
    if (vdef) {
      const v = bus.get(visualModParam(vt), MODIFIER_DEFAULT);
      text = `${vdef.label} · ${vdef.modLabel}`;
      valText = `${Math.round(v * 100)}%`;
      fill = v;
      changeVal = v;
    }
  } else {
    const target = modifiers.target;
    const def = target ? effectByEffect.get(target) : undefined;
    if (def) {
      const v = bus.get(modParam(def.effect), MODIFIER_DEFAULT);
      text = `${def.mnemonic} · ${def.modLabel}`;
      valText = `${Math.round(v * 100)}%`;
      fill = v;
      changeVal = v;
    } else if (audioReactor.active) {
      // Nothing else held while music reactivity runs → +/- scrubs sensitivity.
      const v = audioReactor.sensitivity;
      text = 'Music · Sensitivity';
      valText = `${Math.round(v * 100)}%`;
      fill = v;
      changeVal = v;
    }
  }
  // Flag active movement so the meter can brighten while +/- is actually ramping.
  const changing = changeVal !== modifyLastValue && text !== '' && modifyLastValue >= 0;
  modifyLastValue = text ? changeVal : -1;

  if (text !== modifyLabelText) {
    modifyLabelText = text;
    if (modifyLblEl) modifyLblEl.textContent = text;
    modifyLabelEl.classList.toggle('show', text !== '');
  }
  if (text) {
    if (valText !== modifyValText) {
      modifyValText = valText;
      if (modifyValEl) modifyValEl.textContent = valText;
    }
    if (modifyFillEl) {
      modifyFillEl.style.width = `${Math.round(fill * 100)}%`;
      // Inline gradient for palette; '' reverts to the CSS level colour otherwise.
      if (swatchBg !== modifyFillBg) {
        modifyFillBg = swatchBg;
        modifyFillEl.style.background = swatchBg;
      }
    }
    // A gradient swatch shows its own colours — don't recolour it to the teal glow.
    modifyLabelEl.classList.toggle('is-changing', changing && swatchBg === '');
    modifyLabelEl.classList.toggle('show-swatch', swatchBg !== '');
  }
};

// The +/- scrub ramps continuously while held, so it advances per-frame rather
// than off discrete key events.
const modifierTick = (): void => {
  const now = performance.now();
  // Precedence for the shared +/- keys: palette base hue > visual modifier > effect
  // modifier. A held palette key yields nothing; a held visual key suppresses the
  // effect knob (but not palette); the effect knob runs only when neither wins.
  palette.tick(now);
  visualMod.tick(now, palette.hasHeldPaletteKey);
  modifiers.tick(now, palette.hasHeldPaletteKey || visualMod.target !== undefined);
  // Music sensitivity is the lowest-precedence +/- target: only when nothing else
  // is held does +/- scrub it (and only while music reactivity is active).
  audioReactor.tick(
    now,
    palette.hasHeldPaletteKey || visualMod.target !== undefined || modifiers.target !== undefined,
  );
  autoPilot.tick(now);
  // Global zoom/speed ramp independently — no shared-key precedence, their own keys.
  globals.tick(now);
  refreshModifyLabel();
  requestAnimationFrame(modifierTick);
};
requestAnimationFrame(modifierTick);

// ── Render layer: Three.js scene, reads only the bus ──────────────────
const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
const visualizer = canvas ? new Visualizer(canvas, bus) : undefined;
const webglOk = visualizer?.mount() ?? false;

// ── Second display: publish the performance for /prototypes/glyph/display/ ────────
// Purely additive — this page keeps its own live canvas and dock either way, and
// with no mirror open the channel has no listeners, so it costs nothing.
const displayBtnEl = document.getElementById('display-btn');
const mirror = visualizer
  ? attachMirrorSender(bus, visualizer, {
      onReceiver: () => displayBtnEl?.classList.add('is-live'),
      onReceiverGone: () => displayBtnEl?.classList.remove('is-live'),
    })
  : undefined;
if (displayBtnEl) attachDisplayButton(displayBtnEl);

// ── Bus-only observers ────────────────────────────────────────────────
// (the keyboard dock is built above, before KeyState.)
buildTempoIndicator();
buildComboIndicator();

// ── Debug handle ──────────────────────────────────────────────────────
declare global {
  interface Window {
    __glyph: {
      bus: ParamBus;
      keyState: KeyState;
      controller: InputController;
      modifiers: ModifierController;
      visualMod: ModifierController;
      globals: GlobalController;
      palette: PaletteController;
      autoPilot: AutoPilot;
      tapTempo: TapTempo;
      audioReactor: AudioReactor;
      combos: ComboDetector;
      visualizer?: Visualizer;
      webglOk: boolean;
      /** Detach the second-display link (undefined if there's no scene). */
      mirror?: () => void;
    };
  }
}
window.__glyph = { bus, keyState, controller, modifiers, visualMod, globals, palette, autoPilot, tapTempo, audioReactor, combos, visualizer, webglOk, mirror };

// eslint-disable-next-line no-console
console.log(
  '%cGlyph%c full key set%c\n' +
    'Hold a letter for a momentary effect; Shift+letter latches it on (Shift again releases).\n' +
    'Tab rolls random effects while held, Shift+Tab latches them. 1–7 base visual, 8–0 palette.\n' +
    'Hold an effect key OR a visual key (1–7) and press +/- to tune its modifier; hold a\n' +
    'palette key (8–0) and press +/- to shift that palette’s base hue (tap nudges, hold cycles).\n' +
    '~ rerolls the look, \\ toggles auto-pilot (rolls on the beat), Esc resets.\n' +
    'Tap Space for tempo, hold for live mode. Backspace clears all effects.\n' +
    'Everything routes through [paramBus]; the scene only reads it. window.__glyph.bus.snapshot()',
  'color:#8B31CC;font-weight:800;font-size:14px',
  'color:#2D6BFF',
  'color:inherit',
);
if (!webglOk && canvas) {
  // eslint-disable-next-line no-console
  console.warn('[glyph] Scene not rendering (no WebGL). Input + paramBus still active.');
}

/** Relative so it resolves under whatever path /prototypes/glyph/ is served from. */
const DISPLAY_URL = './display/';
/** Named target, so repeat clicks reuse the window instead of stacking them. */
const DISPLAY_TARGET = 'glyph-display';

/**
 * Minimal shape of the Window Management API. Not in lib.dom yet, and absent
 * outside Chromium — every use is behind a feature check.
 */
interface ScreenPlacement {
  readonly availLeft: number;
  readonly availTop: number;
  readonly availWidth: number;
  readonly availHeight: number;
  readonly isPrimary: boolean;
}

/**
 * Where to put the mirror window. Returns a non-primary screen's bounds when the
 * browser will tell us (Chromium, and only if the user grants window management),
 * otherwise undefined — in which case the window opens wherever and gets dragged
 * across by hand, which is the normal path on Safari and Firefox.
 */
async function secondScreen(): Promise<ScreenPlacement | undefined> {
  const api = (window as unknown as { getScreenDetails?: () => Promise<{ screens: ScreenPlacement[] }> })
    .getScreenDetails;
  if (typeof api !== 'function') return undefined;
  try {
    const details = await api.call(window);
    return details.screens.find((s) => !s.isPrimary);
  } catch {
    return undefined; // permission denied, or no second screen — fall back.
  }
}

/**
 * The display button: open — or re-focus — `/prototypes/glyph/display/` in its own window.
 *
 * Placement is deliberately the only thing we automate. Fullscreen has to be
 * triggered inside that window (`F`, or its own button), which the mirror page
 * says on its waiting card, so there's nothing here that silently half-works.
 */
function attachDisplayButton(btn: HTMLElement): void {
  let win: Window | null = null;
  btn.addEventListener('click', () => {
    if (win && !win.closed) {
      win.focus();
      return;
    }
    void secondScreen().then((screen) => {
      const features = screen
        ? `popup,left=${screen.availLeft},top=${screen.availTop},width=${screen.availWidth},height=${screen.availHeight}`
        : 'popup,width=1280,height=720';
      win = window.open(DISPLAY_URL, DISPLAY_TARGET, features);
      // Focus stays with the mirror after window.open, but the keys are played
      // here — so hand focus straight back rather than making that a manual step.
      window.focus();
    });
  });
}

/**
 * A representative CSS background for a palette type at a given base hue (0..1),
 * previewing the type's colour relationship and tracking the live hue. Every type
 * but Spectrum renders as **discrete hard-edged blocks** — the actual colours the
 * palette selects — rather than a blended gradient, so the swatch reads as a real
 * palette. Spectrum stays a smooth full-wheel rainbow (it has no discrete stops).
 * Mirrors the shader's per-type intent (see shaders.ts `palette()`).
 */
function paletteSwatch(id: string, hue: number): string {
  const deg = (h: number): string => ((((h % 1) + 1) % 1) * 360).toFixed(0);
  const hsl = (h: number, s: number, l: number): string => `hsl(${deg(h)}, ${s}%, ${l}%)`;
  // Hard-stop blocks: each colour fills an equal slice with no blend between them.
  const blocks = (colors: string[]): string => {
    const step = 100 / colors.length;
    const stops = colors
      .map((c, i) => `${c} ${(i * step).toFixed(2)}% ${((i + 1) * step).toFixed(2)}%`)
      .join(', ');
    return `linear-gradient(90deg, ${stops})`;
  };
  switch (id) {
    case 'mono':
      return blocks([hsl(hue, 55, 30), hsl(hue, 60, 62)]);
    case 'analogous':
      return blocks([hsl(hue - 1 / 12, 78, 55), hsl(hue, 82, 55), hsl(hue + 1 / 12, 78, 55)]);
    case 'complementary':
      return blocks([hsl(hue, 82, 55), hsl(hue + 0.5, 82, 55)]);
    case 'triadic':
      return blocks([hsl(hue, 82, 55), hsl(hue + 1 / 3, 82, 55), hsl(hue + 2 / 3, 82, 55)]);
    default: // spectrum — smooth full wheel from the base hue
      return `linear-gradient(90deg, ${hsl(hue, 85, 55)}, ${hsl(hue + 0.2, 85, 55)}, ${hsl(hue + 0.4, 85, 55)}, ${hsl(hue + 0.6, 85, 55)}, ${hsl(hue + 0.8, 85, 55)}, ${hsl(hue + 1, 85, 55)})`;
  }
}

/** Handles the key handlers need from the dock: the lock cell (Enter → panel
 *  lock) and the modifier cells keyed by their normalized key identity. */
interface KeyboardHandles {
  lockCell?: HTMLElement;
  mods: Map<string, HTMLElement>;
}

/**
 * The dock, laid out like a physical keyboard — one continuous number row plus
 * QWERTY letter rows, framed by the modifier keys that also do something. Every
 * modifier key wears the same legend + caption as the letter keys, so what it
 * does reads at a glance:
 *   number row — `~` Reroll · 1–7 visual · 8–0 palette · − + Modify · ⌫ Clear
 *   Tab row    — Tab Random · Q–P · [ ] (inert) · `\` Auto-pilot
 *   home row   — Caps (inert) · A–L · ; Blackout · ’ Whiteout · Enter Panel
 *   Shift row  — Shift Latch · Z–M · < > (inert) · ? Music · Shift Latch
 *   space row  — Space Tap tempo
 * The wider modifier caps create the natural QWERTY staircase. The remaining
 * punctuation keys (< >, [ ]) are inert keycaps for now (labelled, ready to bind
 * later); `?` toggles music reactivity. Letter/visual/preset
 * cells are bus-only observers (held/latched/selected highlight); the functional
 * modifier cells are lit by the key handlers via the returned `mods` map.
 */
function buildKeyboard(): KeyboardHandles {
  const mods = new Map<string, HTMLElement>();
  const root = document.getElementById('keyboard');
  if (!root) return { mods };

  const effectCells = new Map<string, HTMLElement>();  // fx.* param     -> cell
  const visualCells = new Map<number, HTMLElement>();  // visual index   -> cell
  const paletteCells = new Map<number, HTMLElement>(); // palette index  -> swatch span

  // Functional modifier keys that pointer input drives through KeyState (see
  // dockPointer). Enter keeps its own PanelController click; Caps is still inert. The
  // global camera keys ([ ] < >) route as momentary holds — a tap nudges, a held
  // press ramps — so they get a data-key / pointer binding like the letter effects.
  const routableModKeys = new Set([
    'Backquote', 'Minus', 'Equal', 'Backspace', 'Tab', 'ShiftLeft', 'ShiftRight', 'Backslash', 'Slash', 'Space',
    'BracketLeft', 'BracketRight', 'Comma', 'Period',
  ]);

  // A wide modifier keycap: a legend + a small caption, styled like a letter key.
  // `--span` sets the width in key units. `caption` may be '' for an inert key.
  const makeMod = (dataKey: string, legend: string, caption: string, span: number, cls = ''): HTMLElement => {
    const cell = document.createElement('div');
    cell.className = `kb-key kb-mod ${cls}`.trim();
    cell.style.setProperty('--span', String(span));
    cell.innerHTML = `<span class="cap">${legend}</span>` + (caption ? `<span class="name">${caption}</span>` : '');
    // Tag only the functional mods so dockPointer wires just those to KeyState.
    if (routableModKeys.has(dataKey)) cell.dataset.key = dataKey;
    mods.set(dataKey, cell);
    return cell;
  };

  // ── Section labels above the number row: "Visual" over 1–7, "Palette" over
  //    8–0, and the live "modify" target over the −/+ keys. Each label spans the
  //    same key-units as the group it sits over, sized by the shared pitch model
  //    (see .kb-group-label), so its edges line up with the keys beneath it. ──
  const makeLabel = (span: number, text: string, id = '', cls = '', bracket = false): HTMLElement => {
    const el = document.createElement('span');
    el.className = `kb-group-label ${cls}`.trim();
    el.style.setProperty('--span', String(span));
    if (id) el.id = id;
    // A group label stacks its text over a downward "bracket" that embraces the
    // keys below it, making it obvious which keys the label covers.
    if (text || bracket) {
      el.innerHTML =
        (text ? `<span class="lbl">${text}</span>` : '') +
        (bracket ? '<span class="group-bracket"></span>' : '');
    }
    return el;
  };
  const labelRow = document.createElement('div');
  labelRow.className = 'kb-row kb-labels';
  labelRow.appendChild(makeLabel(1, ''));                                    // over ~
  labelRow.appendChild(makeLabel(7, 'Base Visual', '', 'label-visual', true)); // over 1–7
  labelRow.appendChild(makeLabel(3, 'Palette', '', 'label-palette', true));  // over 8–0
  labelRow.appendChild(makeLabel(2, '', 'modify-label'));                    // over − +
  labelRow.appendChild(makeLabel(2, ''));                                    // over ⌫ — pads the row to a full 15u so it lines up with the key rows
  root.appendChild(labelRow);

  // ── Number row: ~ · 1–7 visual · 8–0 palette · Backspace ──
  const numRow = document.createElement('div');
  numRow.className = 'kb-row kb-nums';
  const tildeCell = makeMod('Backquote', '~', 'Reroll', 1, 'tilde-cap');
  numRow.appendChild(tildeCell);
  for (const v of visualConfig) {
    const cell = document.createElement('div');
    cell.className = 'kb-key vis-key';
    cell.dataset.key = v.key;
    cell.innerHTML =
      `<span class="cap">${v.key}</span>` +
      `<span class="name">${v.label}</span>` +
      `<span class="mode">visual</span>`;
    numRow.appendChild(cell);
    visualCells.set(v.index, cell);
  }
  for (const p of paletteConfig) {
    const cell = document.createElement('div');
    cell.className = 'kb-key preset-key';
    cell.dataset.key = p.key;
    cell.innerHTML =
      `<span class="cap">${p.key}</span>` +
      `<span class="name">${p.label}</span>`;
    numRow.appendChild(cell);
    paletteCells.set(p.index, cell);
  }
  const minusCell = makeMod('Minus', '−', 'Tune', 1);
  numRow.appendChild(minusCell);
  const plusCell = makeMod('Equal', '+', 'Tune', 1);
  numRow.appendChild(plusCell);
  const bkspCell = makeMod('Backspace', '⌫', 'Clear', 2);
  numRow.appendChild(bkspCell);
  root.appendChild(numRow);

  // ── Letter rows, in QWERTY order, framed by their real modifier keys ──
  // Caps is an inert keycap (kept for the real-keyboard staircase); the panel
  // lock lives on Enter, added at the end of the home row below.
  const lead: Record<string, HTMLElement> = {
    QWERTYUIOP: makeMod('Tab', 'Tab', 'Random', 1.5),
    ASDFGHJKL: makeMod('CapsLock', 'Caps', '', 1.75),
    ZXCVBNM: makeMod('ShiftLeft', 'Shift', 'Latch', 2.25),
  };
  // Build one effect keycap: the big cap glyph (`def.cap ?? def.key`, so `;`/`'`
  // show a glyph rather than 'Semicolon'/'Quote'), the mnemonic caption, and the
  // effect/latch registrations that drive the active(violet)/latched(amber) glow.
  const makeEffectCell = (def: EffectDef): HTMLElement => {
    const cell = document.createElement('div');
    cell.className = 'kb-key key-cell';
    cell.dataset.key = def.key;
    // The only tooltip that survives on an effect key: what the effect looks
    // like (its mnemonic is already the caption; the hint isn't shown anywhere).
    cell.title = def.hint;
    cell.innerHTML =
      `<span class="cap">${def.cap ?? def.key}</span>` +
      `<span class="name">${def.mnemonic}</span>`;
    effectCells.set(effectParam(def.effect), cell);
    // Latch flag shares the cell, keyed by the `latch.<e>` param.
    effectCells.set(latchParam(def.effect), cell);
    return cell;
  };

  const rows = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
  for (const rowStr of rows) {
    const row = document.createElement('div');
    row.className = 'kb-row';
    row.appendChild(lead[rowStr]);
    for (const ch of rowStr) {
      const def = effectByKey.get(ch);
      if (!def) continue;
      row.appendChild(makeEffectCell(def));
    }
    // Fill out each row with its real trailing keys. The bracket pair is the global
    // Zoom dolly ([ out, ] in) and the angle pair is global Speed (< slower/reverse,
    // > faster); the other functional keys keep their captions: `\` auto-pilot, Enter
    // panel lock, right Shift latch.
    if (rowStr === 'QWERTYUIOP') {
      row.appendChild(makeMod('BracketLeft', '[', 'Zoom −', 1));
      row.appendChild(makeMod('BracketRight', ']', 'Zoom +', 1));
      row.appendChild(makeMod('Backslash', '\\', 'Auto-pilot', 1.5));
    } else if (rowStr === 'ASDFGHJKL') {
      // `;` Blackout and `'` Whiteout — real effect keycaps (not inert), so they
      // wear the standard held/latched highlights like every letter effect.
      const semi = effectByKey.get('Semicolon');
      const quote = effectByKey.get('Quote');
      if (semi) row.appendChild(makeEffectCell(semi));
      if (quote) row.appendChild(makeEffectCell(quote));
      // Enter keeps a tooltip — its 3-state lock cycle is set live in reflectLock().
      row.appendChild(makeMod('Enter', 'Enter', 'Panel', 2.25, 'enter-key'));
    } else if (rowStr === 'ZXCVBNM') {
      row.appendChild(makeMod('Comma', '<', 'FX Speed −', 1));
      row.appendChild(makeMod('Period', '>', 'FX Speed +', 1));
      row.appendChild(makeMod('Slash', '?', 'Music', 1));
      row.appendChild(makeMod('ShiftRight', 'Shift', 'Latch', 2.75));
    }
    root.appendChild(row);
  }

  // ── Space bar row: tap for tempo, hold for live mode. The BPM readout lives
  //    inside the bar, and buildTempoIndicator() pulses the bar's whole outline
  //    on the beat (see #space-key). ──
  const spaceRow = document.createElement('div');
  spaceRow.className = 'kb-row kb-space';
  const space = makeMod('Space', 'Space', '', 9, 'space-key');
  space.id = 'space-key';
  const bpm = document.createElement('span');
  bpm.className = 'name';
  bpm.innerHTML = '<b id="tempo-bpm">––</b> BPM';
  space.appendChild(bpm);
  spaceRow.appendChild(space);
  root.appendChild(spaceRow);

  // Highlight the seeded base visual + palette type before any keypress.
  visualCells.get(Math.round(bus.get(VISUAL_PARAM, 0)))?.classList.add('active');
  let activePalette = Math.round(bus.get(PALETTE_TYPE_PARAM, 0));
  paletteCells.get(activePalette)?.classList.add('active');

  const readout = document.getElementById('readout');
  const visualLabel = new Map(visualConfig.map((v) => [v.index, v.label]));
  const paletteLabel = new Map(paletteConfig.map((p) => [p.index, p.label]));

  bus.subscribe((change) => {
    const cell = effectCells.get(change.key);
    if (cell) {
      // `fx.<e>` drives the on glow; `latch.<e>` marks the persistent ring.
      if (change.key.startsWith('fx.')) cell.classList.toggle('active', change.value > 0);
      else if (change.key.startsWith('latch.')) cell.classList.toggle('latched', change.value > 0);
    }
    if (change.key === VISUAL_PARAM) {
      const idx = Math.round(change.value);
      for (const [i, c] of visualCells) c.classList.toggle('active', i === idx);
    }
    if (change.key === PALETTE_TYPE_PARAM) {
      activePalette = Math.round(change.value);
      for (const [i, c] of paletteCells) c.classList.toggle('active', i === activePalette);
    }
    if (readout) {
      if (change.key === VISUAL_PARAM) {
        readout.textContent = `visual → ${visualLabel.get(Math.round(change.value)) ?? change.value}`;
      } else if (change.key === PALETTE_TYPE_PARAM) {
        readout.textContent = `palette → ${paletteLabel.get(Math.round(change.value)) ?? change.value}`;
      } else if (change.key === PALETTE_HUE_PARAM) {
        readout.textContent = `${paletteLabel.get(activePalette) ?? 'palette'} · ${Math.round(change.value * 360)}°`;
      } else if (change.key.startsWith('fx.')) {
        const prev = change.previous === undefined ? '—' : change.previous.toFixed(2);
        readout.textContent = `${change.key}  ${prev} → ${change.value.toFixed(2)}`;
      }
    }
  });

  return { lockCell: mods.get('Enter'), mods };
}

/**
 * Combo indicator — a bus-only observer. When a `combo.*` param goes positive it
 * flashes the combo's label; when every combo returns to 0 it hides. Purely
 * reactive: it never sees a key, only the bus.
 */
function buildComboIndicator(): void {
  const el = document.getElementById('combo-flash');
  if (!el) return;

  const labelByParam = new Map(comboConfig.map((c) => [`combo.${c.name}`, c.label]));
  const activeParams = new Set<string>();

  bus.subscribe((change) => {
    if (!labelByParam.has(change.key)) return;
    if (change.value > 0) {
      activeParams.add(change.key);
      el.textContent = labelByParam.get(change.key) ?? '';
      el.classList.add('show');
    } else {
      activeParams.delete(change.key);
      if (activeParams.size === 0) el.classList.remove('show');
      else el.textContent = labelByParam.get([...activeParams].at(-1)!) ?? '';
    }
  });
}

/**
 * Tempo indicator — reads bpm / anchor / live from the bus and derives the beat
 * phase in its own rAF, pulsing the whole Space bar's outline on each beat so
 * taps feel confirmed. The BPM number lives inside the Space bar too; before any
 * tap (bpm 0) the inline outline is cleared so the bar looks like a normal key.
 */
function buildTempoIndicator(): void {
  const spaceKey = document.getElementById('space-key');
  const bpmEl = document.getElementById('tempo-bpm');
  if (!spaceKey || !bpmEl) return;

  const BLUE = '45,107,255';
  const PINK = '255,45,120';
  let bpm = 0;
  let anchor = 0;
  let live = false;

  bus.subscribe((change) => {
    if (change.key === 'tempo.bpm') {
      bpm = change.value;
      bpmEl.textContent = bpm > 0 ? String(Math.round(bpm)) : '––';
    } else if (change.key === 'tempo.anchorMs') {
      anchor = change.value;
    } else if (change.key === 'tempo.live') {
      live = change.value > 0;
      spaceKey.classList.toggle('live', live);
    }
  });

  const tick = (): void => {
    if (bpm > 0 && anchor > 0) {
      const beatMs = 60000 / bpm;
      const phase = ((((performance.now() - anchor) % beatMs) + beatMs) % beatMs) / beatMs;
      const env = (1 - phase) ** 3;
      const rgb = live ? PINK : BLUE;
      spaceKey.style.borderColor = `rgba(${rgb},${(0.25 + env * 0.75).toFixed(3)})`;
      spaceKey.style.boxShadow =
        `0 0 0 1px rgba(${rgb},${(0.2 + env * 0.8).toFixed(3)}), ` +
        `0 0 ${(env * 26).toFixed(1)}px rgba(${rgb},${(env * 0.55).toFixed(3)})`;
    } else {
      // No tempo yet — leave the Space bar looking like an ordinary key.
      spaceKey.style.borderColor = '';
      spaceKey.style.boxShadow = '';
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
